import { ref, onUnmounted } from 'vue'
import { downloadFile } from '@/api/universal-file'

/**
 * 节点图标管理 Composable
 * 统一管理节点图标的加载、缓存和清理
 */
export function useNodeIcon() {
  // 图标URL缓存 (fileId -> blob URL)
  const iconUrls = ref<Record<string, string>>({})

  /**
   * 加载单个图标
   * @param fileId 文件ID
   * @returns blob URL 或空字符串
   */
  const loadIcon = async(fileId: string): Promise<string> => {
    if (!fileId) return ''

    // 如果已缓存，直接返回
    if (iconUrls.value[fileId]) {
      return iconUrls.value[fileId]
    }

    try {
      const fileBlob = await downloadFile(fileId)
      if (fileBlob) {
        const url = URL.createObjectURL(fileBlob)
        iconUrls.value = { ...iconUrls.value, [fileId]: url }
        return url
      }
    } catch (error) {
      console.error('[useNodeIcon] 加载图标失败:', fileId, error)
    }

    return ''
  }

  /**
   * 批量加载图标
   * @param fileIds 文件ID数组
   */
  const loadIcons = async(fileIds: string[]) => {
    const validFileIds = fileIds.filter(id => id && !iconUrls.value[id])

    await Promise.all(
      validFileIds.map(fileId => loadIcon(fileId))
    )
  }

  /**
   * 获取图标URL
   * @param fileId 文件ID
   * @returns blob URL 或空字符串
   */
  const getIconUrl = (fileId?: string): string => {
    if (!fileId) return ''
    return iconUrls.value[fileId] || ''
  }

  /**
   * 清理指定图标
   * @param fileId 文件ID
   */
  const revokeIcon = (fileId: string) => {
    const url = iconUrls.value[fileId]
    if (url) {
      URL.revokeObjectURL(url)
      const newUrls = { ...iconUrls.value }
      delete newUrls[fileId]
      iconUrls.value = newUrls
    }
  }

  /**
   * 清理所有图标
   */
  const revokeAllIcons = () => {
    Object.values(iconUrls.value).forEach(url => URL.revokeObjectURL(url))
    iconUrls.value = {}
  }

  // 组件卸载时自动清理
  onUnmounted(() => {
    revokeAllIcons()
  })

  return {
    iconUrls,
    loadIcon,
    loadIcons,
    getIconUrl,
    revokeIcon,
    revokeAllIcons
  }
}
